const guide = `# Provider Portal Documentation

## Overview

The Provider Portal enables providers to request eSIMs, manage inventory, assign eSIMs to customers, and track activation status. This guide outlines the complete workflow, available APIs, UI requirements, and system behavior.

**Base API Path:** \`https://r4lsolutions.com/backend/api/provider\`

> All endpoints require a valid Provider Authentication Token.

---

# eSIM Status Mapping

The backend returns internal status values that should be displayed using user-friendly labels.

| Backend Status           | Display Label          |
| ------------------------ | ---------------------- |
| \`waiting_for_assignment\` | In Stock               |
| \`pre_service\`            | Waiting for Activation |
| \`in_service\`             | Active                 |
| \`terminated\`             | Terminated             |

---

# Request Status Mapping

| Status     | Meaning                                       |
| ---------- | --------------------------------------------- |
| \`pending\`  | Request submitted and awaiting approval       |
| \`approved\` | Request approved and eSIMs added to inventory |
| \`rejected\` | Request declined                              |

---

# Provider Workflow

\`\`\`text
Request SIMs
      ↓
Request Approval
      ↓
eSIM Added to Inventory
      ↓
Assign eSIM to Customer
      ↓
Generate QR Code
      ↓
Customer Activates eSIM
      ↓
Sync Status
      ↓
eSIM Becomes Active
\`\`\`

---

# 1. Request eSIMs

Providers can submit requests for new eSIM inventory.

### API

\`\`\`http
POST https://r4lsolutions.com/backend/api/provider/sim-requests
\`\`\`

### Request Body

\`\`\`json
{
  "quantity": 5,
  "note": "Need for Q3 campaign"
}
\`\`\`

### Validation

| Field    | Type   | Required | Notes               |
| -------- | ------ | -------- | ------------------- |
| quantity | Number | Yes      | Range: 1–500        |
| note     | String | No       | Additional comments |

### Expected Behavior

* Create a new request.
* Request status is automatically set to \`pending\`.
* Newly created request should appear in the Requests List.

---

# 2. View My SIM Requests

Providers can track submitted requests and monitor approval status.

### API

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/sim-requests
\`\`\`

### Example

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/sim-requests?page=1&pageSize=20&status=pending
\`\`\`

### Available Filters

| Filter    | Description                 |
| --------- | --------------------------- |
| status    | pending, approved, rejected |
| search    | Search by Request ID        |
| startDate | Filter by creation date     |
| endDate   | Filter by creation date     |

### Statistics API

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/sim-requests/stats
\`\`\`

### UI Requirements

Display:

* Request ID
* Quantity
* Notes
* Created Date
* Status Badge

### Status Badges

* Pending
* Approved
* Rejected

> Once a request is approved, the associated eSIMs automatically become available in the Inventory section.

---

# 3. eSIM Inventory Management

Inventory displays all eSIMs assigned to the provider.

### API

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/esims
\`\`\`

### Example

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/esims?assigned=false&status=pre_service&page=1&pageSize=20
\`\`\`

### Available Filters

| Filter    | Description       |
| --------- | ----------------- |
| assigned  | true / false      |
| status    | eSIM status       |
| search    | ICCID search      |
| startDate | Date range filter |
| endDate   | Date range filter |

### Statistics API

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/esims/stats
\`\`\`

### Response Fields

| Field           | Description                  |
| --------------- | ---------------------------- |
| iccid           | Unique eSIM identifier       |
| activation_code | Activation code (LPA string) |
| status          | Current eSIM status          |
| customer_id     | Assigned customer            |
| activated_at    | Activation timestamp         |

### UI Requirements

Display:

* ICCID
* Status
* Assigned Customer
* Activation Date
* Actions

### Actions

| Action      | Availability          |
| ----------- | --------------------- |
| Assign      | Unassigned eSIMs      |
| Sync Status | All inventory records |

### Inventory Views

**Available to Assign**

\`\`\`text
assigned = false
\`\`\`

Shows eSIMs ready to be assigned to customers.

---

# 4. Assign eSIM to a Customer

This step links an available eSIM to a customer and generates activation credentials.

### API

\`\`\`http
PATCH https://r4lsolutions.com/backend/api/provider/esims/{iccid}/assign
\`\`\`

### Request Body

\`\`\`json
{
  "customer_id": "cust_abc123"
}
\`\`\`

### Response

Returns:

* Assigned eSIM information
* Activation Code (LPA string)

Example:

\`\`\`text
LPA:1$consumer.e-sim.global$TN2025...
\`\`\`

---

## Frontend Requirements After Assignment

### Generate QR Code

The returned \`activation_code\` must be converted into a QR code.

### Display Activation Code

Provide the raw activation code as a fallback option.

Example:

\`\`\`text
LPA:1$consumer.e-sim.global$TN2025...
\`\`\`

### Copy Functionality

Allow the provider to copy the activation code.

### Important

Assigning an eSIM **does not activate it**.

Status remains:

\`\`\`text
Waiting for Activation
\`\`\`

until the customer installs the eSIM profile.

---

# 5. Customer Activation

No provider action is required during activation.

### Process

1. Provider shares QR Code.
2. Customer scans QR Code on their device.
3. Carrier installs the eSIM profile.
4. Backend receives activation confirmation.
5. Status changes automatically:

\`\`\`text
pre_service → in_service
\`\`\`

### Frontend Requirement

No API call is needed.

The provider only needs to refresh or sync inventory status.

---

# 6. Sync Activation Status

Allows providers to fetch the latest activation updates from the carrier.

### API

\`\`\`http
POST https://r4lsolutions.com/backend/api/provider/esims/sync
\`\`\`

### Sample Response

\`\`\`json
{
  "total_checked": 5,
  "total_updated": 2,
  "updated": [
    {
      "iccid": "8910300000041170580",
      "old_status": "pre_service",
      "new_status": "in_service",
      "activated_at": "2026-06-17T08:00:00.000Z"
    }
  ],
  "failed": []
}
\`\`\`

### Frontend Behavior

After a successful sync:

1. Reload inventory data.
2. Update status badges.
3. Display activation timestamps.
4. Move activated eSIMs to the Active state.

### Status Transition

\`\`\`text
Waiting for Activation
          ↓
       Active
\`\`\`

---

# Customer Management

Providers can manage customers through the customer module.

### Customer List API

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/customers
\`\`\`

### Customer Statistics API

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/customers/stats
\`\`\`

---

# Complete API Reference

## SIM Requests

| Method | Endpoint                           | Description            |
| ------ | ---------------------------------- | ---------------------- |
| POST   | \`https://r4lsolutions.com/backend/api/provider/sim-requests\`       | Create new SIM request |
| GET    | \`https://r4lsolutions.com/backend/api/provider/sim-requests\`       | List SIM requests      |
| GET    | \`https://r4lsolutions.com/backend/api/provider/sim-requests/stats\` | Request statistics     |

---

## eSIM Inventory

| Method | Endpoint                             | Description             |
| ------ | ------------------------------------ | ----------------------- |
| GET    | \`https://r4lsolutions.com/backend/api/provider/esims\`                | List eSIM inventory     |
| GET    | \`https://r4lsolutions.com/backend/api/provider/esims/stats\`          | Inventory statistics    |
| PATCH  | \`https://r4lsolutions.com/backend/api/provider/esims/{iccid}/assign\` | Assign eSIM to customer |
| POST   | \`https://r4lsolutions.com/backend/api/provider/esims/sync\`           | Sync activation status  |

---

## Customers

| Method | Endpoint                        | Description         |
| ------ | ------------------------------- | ------------------- |
| GET    | \`https://r4lsolutions.com/backend/api/provider/customers\`       | Customer list       |
| GET    | \`https://r4lsolutions.com/backend/api/provider/customers/stats\` | Customer statistics |

---

# End-to-End Lifecycle

\`\`\`text
Provider Requests eSIMs
          ↓
Request Approved
          ↓
eSIM Added to Inventory
          ↓
Provider Assigns eSIM
          ↓
QR Code Generated
          ↓
Customer Installs eSIM
          ↓
Carrier Confirms Activation
          ↓
Provider Syncs Status
          ↓
eSIM Becomes Active
\`\`\`

This workflow ensures providers can efficiently manage inventory, distribute eSIMs to customers, and monitor activation progress from a single portal.
`;

export default guide;
